/*
 * Healing-property tabs — a self-contained custom element.
 *
 * 1. Tab switching: toggles the active tab/panel. Progressive enhancement —
 *    all panels are rendered server-side, the first visible with no JS.
 * 2. Per-panel carousel: each panel holds a horizontally-scrollable product
 *    track. Floating prev/next arrows scroll it by one "page" (~5 cards). The
 *    arrows are hidden until JS confirms the track overflows, and toggle as the
 *    track reaches either end. Without JS the track still scrolls natively.
 *
 * No framework, no theme base class — plain DOM API, scoped to the element.
 */
(function () {
  if (customElements.get('healing-property-tabs')) return;

  class HealingPropertyTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('.healing-tabs__tab'));
      this.panels = this.tabs.map((tab) =>
        this.querySelector('#' + CSS.escape(tab.getAttribute('aria-controls')))
      );
      this.carousels = new Array(this.tabs.length);

      // Each panel (rendered by the block, which can resolve the collection)
      // carries its collection title in data-tab-label. Copy it onto the
      // matching tab button — the section that builds the tab strip can't read
      // a theme block's collection setting itself.
      this.tabs.forEach((tab) => {
        const id = tab.getAttribute('aria-controls');
        const panel = this.querySelector('#' + CSS.escape(id));
        const template = this.querySelector(`template[data-healing-panel-template="${CSS.escape(id)}"]`);
        const label = panel?.getAttribute('data-tab-label') || template?.getAttribute('data-tab-label');
        if (label) tab.textContent = label;
      });

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
      });

      this.panels.forEach((panel, index) => this.preparePanel(panel, index));

      // Hand panel visibility to JS: mark each panel managed, then show the
      // first. (Before this the CSS no-JS fallback shows only the first panel.)
      const active = this.tabs.findIndex((t) => t.classList.contains('is-active'));
      this.select(active < 0 ? 0 : active);
    }

    ensurePanel(index) {
      if (this.panels[index]) return this.panels[index];

      const id = this.tabs[index]?.getAttribute('aria-controls');
      const template = id && this.querySelector(`template[data-healing-panel-template="${CSS.escape(id)}"]`);
      if (!template) return null;

      const fragment = template.content.cloneNode(true);
      const panel = fragment.querySelector('.healing-tabs__panel');
      template.replaceWith(fragment);
      this.panels[index] = panel;
      this.preparePanel(panel, index);
      return panel;
    }

    preparePanel(panel, index) {
      if (!panel) return;
      panel.setAttribute('data-js-managed', '');

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        panel.querySelectorAll('.healing-tabs__media--has-secondary').forEach((media) => {
          media.addEventListener('pointerenter', () => this.loadSecondaryImage(media), { once: true });
          media.addEventListener('focusin', () => this.loadSecondaryImage(media), { once: true });
        });
      }

      const carousel = panel.querySelector('[data-healing-carousel]');
      this.carousels[index] = carousel ? this.initCarousel(carousel) : null;
    }

    loadSecondaryImage(media) {
      const image = media.querySelector('.healing-tabs__img--secondary[data-hover-src]');
      if (!image) return;
      image.addEventListener('load', () => media.classList.add('is-secondary-ready'), { once: true });
      image.srcset = image.dataset.hoverSrcset || '';
      image.src = image.dataset.hoverSrc;
      image.removeAttribute('data-hover-src');
      image.removeAttribute('data-hover-srcset');
    }

    select(index) {
      this.ensurePanel(index);
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.panels.forEach((panel, i) => {
        if (!panel) return;
        if (i === index) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
      // A track hidden at load reports zero width; refresh arrows once shown.
      if (this.carousels && this.carousels[index]) this.carousels[index].update();
    }

    onKeydown(e, index) {
      const last = this.tabs.length - 1;
      let next = null;
      if (e.key === 'ArrowRight') next = index === last ? 0 : index + 1;
      else if (e.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      if (next === null) return;
      e.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }

    initCarousel(root) {
      const track = root.querySelector('[data-carousel-track]');
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      if (!track) return { update() {} };

      const page = () => track.clientWidth * 0.9; // ~one screen of cards
      const update = () => {
        const overflow = track.scrollWidth - track.clientWidth > 2;
        const atStart = track.scrollLeft <= 2;
        const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.hidden = !overflow || atStart;
        if (next) next.hidden = !overflow || atEnd;
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -page(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: page(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      update();
      return { update };
    }
  }

  customElements.define('healing-property-tabs', HealingPropertyTabs);
})();
